/** Maximum number of records submitted in a single import request. */
import * as moment from "moment";
export const MAX_IMPORT_RECORDS = 2000;

export type PVRFieldKey =
  | "Title"
  | "Contractor"
  | "Agency"
  | "SerialNumber"
  | "ConductedBy"
  | "VerificationDate"
  | "Summary";

export type FieldMapping = Partial<Record<PVRFieldKey, string>>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

export const PVR_FIELDS: Array<{
  key: PVRFieldKey;
  label: string;
  required?: boolean;
}> = [
  { key: "Title", label: "Title", required: true },
  { key: "Contractor", label: "Contractor", required: true },
  { key: "Agency", label: "Agency", required: true },
  { key: "SerialNumber", label: "Serial Number" },
  { key: "ConductedBy", label: "Conducted By" },
  { key: "VerificationDate", label: "Verification Date" },
  { key: "Summary", label: "Summary" },
];

function normalizeStr(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function similarityScore(a: string, b: string): number {
  if (a === b) return 2;
  if (a.includes(b) || b.includes(a)) {
    return Math.min(a.length, b.length) / Math.max(a.length, b.length);
  }
  return 0;
}

export function autoMapFields(csvHeaders: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of PVR_FIELDS) {
    const normField = normalizeStr(field.key);
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const score = similarityScore(normField, normalizeStr(header));
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping,
) {
  const get = (key: PVRFieldKey): string | null => {
    const col = mappings[key];
    if (!col) return null;
    return row[col]?.trim() || null;
  };

  return {
    title: get("Title") ?? "",
    contractor: get("Contractor"),
    agency: get("Agency"),
    serialNumber: get("SerialNumber"),
    conductedBy: get("ConductedBy"),
    verificationDate:
      (get("VerificationDate") &&
        moment(get("VerificationDate"), "DD/MM/YYYY")
          .toDate()
          .toLocaleDateString("en-CA")) ||
      null,
    summary: get("Summary") ?? "",
  };
}
